import React from "react";

let createVertexShader = function(gl) {
  let shader = gl.createShader(gl.VERTEX_SHADER);
  let script = `
		precision highp float;

		attribute vec2 position;
		uniform vec2 translate;
		uniform vec2 scale;

		varying vec2 v;

		void main(void) {
			gl_Position = vec4(position, 0., 1.);
			v = vec2(scale.x*(position.x+translate.x), scale.y*(position.y+translate.y));
		}
		`;
  gl.shaderSource(shader, script);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error("Error in shader: ", gl.getShaderInfoLog(shader));
    console.log(script);
    return false;
  }
  return shader;
};

let packages = {
  cMath: /* Complex */ `
		vec2 cI = vec2(0.,1.), c1 = vec2(1.,0.), c0 = vec2(0.,0.);

		vec2 cmul(vec2 a, vec2 b) {
			return vec2(a.x*b.x-a.y*b.y, a.x*b.y+a.y*b.x);
		}

		vec2 csquare(vec2 a) {
			return vec2(a.x*a.x-a.y*a.y, 2.*a.x*a.y);
		}

		vec2 cmuli(vec2 a) {
			return vec2(-a.y, a.x);
		}

		vec2 cinv(vec2 a) {
			float n = a.x*a.x+a.y*a.y;
			return vec2(a.x/n, -a.y/n);
		}

		vec2 cdiv(vec2 a, vec2 b) {
			return cmul(a, cinv(b));
		}

		vec2 cexp(vec2 a) {
			float e = exp(a.x);
			return vec2(e*cos(a.y), e*sin(a.y));
		}

		vec2 csinh(vec2 a) {
			float e = exp(a.x);
			return .5*(cexp(a)-cexp(-a));
		}

		vec2 csin(vec2 a) {
			return -cmuli(csinh(cmuli(a)));
		}

		vec2 ccosh(vec2 a) {
			return .5*(cexp(a)+cexp(-a));
		}

		vec2 ccos(vec2 a) {
			return ccosh(cmuli(a));
		}

		`,
  pMath: /* Double precision */ `
		vec2 pset(float a) {
			vec2 z;
			z.x = a;
			z.y = 0.0;
			return z;
		}

		vec2 padd (vec2 dsa, vec2 dsb) {
			vec2 dsc;
			float t1, t2, e;

			t1 = dsa.x + dsb.x;
			e = t1 - dsa.x;
			t2 = ((dsb.x - e) + (dsa.x - (t1 - e))) + dsa.y + dsb.y;

			dsc.x = t1 + t2;
			dsc.y = t2 - (dsc.x - t1);
			return dsc;
		}

		vec2 psub (vec2 dsa, vec2 dsb) {
			vec2 dsc;
			float e, t1, t2;

			t1 = dsa.x - dsb.x;
			e = t1 - dsa.x;
			t2 = ((-dsb.x - e) + (dsa.x - (t1 - e))) + dsa.y - dsb.y;

			dsc.x = t1 + t2;
			dsc.y = t2 - (dsc.x - t1);
			return dsc;
		}


		float pcompare(vec2 dsa, vec2 dsb) {
			if (dsa.x < dsb.x) return -1.;
			else if (dsa.x == dsb.x)
			{
				if (dsa.y < dsb.y) return -1.;
				else if (dsa.y == dsb.y) return 0.;
				else return 1.;
			}
			else return 1.;
		}


		vec2 pmul (vec2 dsa, vec2 dsb) {
			vec2 dsc;
			float c11, c21, c2, e, t1, t2;
			float a1, a2, b1, b2, cona, conb, split = 8193.;

			cona = dsa.x * split;
			conb = dsb.x * split;
			a1 = cona - (cona - dsa.x);
			b1 = conb - (conb - dsb.x);
			a2 = dsa.x - a1;
			b2 = dsb.x - b1;

			c11 = dsa.x * dsb.x;
			c21 = a2 * b2 + (a2 * b1 + (a1 * b2 + (a1 * b1 - c11)));

			c2 = dsa.x * dsb.y + dsa.y * dsb.x;

			t1 = c11 + c2;
			e = t1 - c11;
			t2 = dsa.y * dsb.y + ((c2 - e) + (c11 - (t1 - e))) + c21;

			dsc.x = t1 + t2;
			dsc.y = t2 - (dsc.x - t1);

			return dsc;
		}
		`
};

class RunningAverage {
  constructor(count) {
    this.count = count;
    this.currentCount = 0;
    this.elements = [];
    for (let i = 0; i < count; ++i) this.elements.push(0);
    this.i = 0;
    this.sum = 0;
  }

  add(value) {
    if (!isFinite(value)) return;
    this.sum -= this.elements[this.i];
    this.sum += this.elements[this.i] = value;
    ++this.i;
    if (this.i >= this.count) this.i -= this.count;
    if (this.currentCount < this.count) ++this.currentCount;
  }

  get average() {
    return this.sum / this.currentCount;
  }
}
export default class extends React.Component {
  static defaultProps = {
    locations: [],
    start: true,
    scale: [1, 1],
    translate: [0, 0],
    navigatable: false
  };
  state = { scale: [1, 1], translate: [0, 0] };

  constructor(...args) {
    super(...args);

    this.translate = this.props.translate;
    this.scale = this.props.scale;
    this.canvas = document.createElement("canvas");
    this.canvas.style.width = "100%";
    this.canvas.style.height = "100%";

    this.invalidated = true;

    this.fps = new RunningAverage(100);
    this.time = 0;
    this.gl = this.canvas.getContext("webgl", { antialias: true });
    if (this.props.shader) {
      this.setFragmentShader(
        this.createFragmentShader(this.props.shader, this.props.libs)
      );
    }
    if (this.props.start) {
      this.disabled = false;
      setTimeout(() => this.tick());
    }
    if (this.props.navigatable) {
      this.makeNavigatable();
    }
  }

  componentDidMount() {
    this.resetZoom();
  }
  componentWillUnmount() {
    this.disabled = true;
  }

  resetZoom() {
    let [sx, sy] = this.scale;
    this.translate = [0, 0];
    if (sx > sy) this.scale = [sx / sy, 1];
    else this.scale = [1, sy / sx];
    this.invalidated = true;
  }

  createProgram(fragmentShader) {
    let gl = this.gl;
    let program = gl.createProgram();
    gl.attachShader(program, createVertexShader(gl));
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS))
      console.error(gl.getProgramInfoLog(program));

    let position = gl.getAttribLocation(program, "position");
    let vertexPosBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, vertexPosBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      gl.STATIC_DRAW
    );
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    return program;
  }

  setFragmentShader(fragmentShader) {
    let gl = this.gl;
    let program = this.createProgram(fragmentShader);
    let locations = (this.locations = {
      translate: gl.getUniformLocation(program, "translate"),
      scale: gl.getUniformLocation(program, "scale")
    });
    this.props.locations.forEach(l => {
      locations[l] = gl.getUniformLocation(program, l);
    });

    gl.useProgram(program);
    this.program = program;
    this.invalidated = true;
  }

  makeNavigatable() {
    this.canvas.addEventListener("mousemove", e => {
      if (e.buttons > 0) {
        this.addTranslate(
          (-2 * e.movementX) / this.width,
          (2 * e.movementY) / this.height
        );
      }
    });

    this.canvas.addEventListener("wheel", e => {
      if (e.deltaY > 0) this.multiplyScale(1.2);
      if (e.deltaY < 0) this.multiplyScale(1 / 1.2);
    });

    let fingers = 0;
    let center = [];
    let distance = 0;
    let memory = null;
    let calculateCenter = touches => {
      let center = [0, 0];
      let n = touches.length;
      for (let i = 0; i < n; ++i) {
        center[0] += (touches[i].clientX - this.width / 2) / n;
        center[1] += (touches[i].clientY - this.height / 2) / n;
      }
      return center;
    };
    let calculateDistance = touches => {
      let x1 = touches[0].clientX,
        y1 = touches[0].clientY;
      let x2 = touches[1].clientX,
        y2 = touches[1].clientY;
      return Math.hypot(x1 - x2, y1 - y2);
    };
    let initializeTouch = e => {
      fingers = e.touches.length;
      center = calculateCenter(e.touches);
      memory = {
        scale: this.scale,
        translate: this.translate
      };
      if (fingers === 1) distance = -1;
      else if (fingers === 2) {
        distance = calculateDistance(e.touches);
        center = calculateCenter(e.touches);
      } else return;
      e.preventDefault();
    };
    this.canvas.addEventListener("touchstart", initializeTouch);
    this.canvas.addEventListener("touchmove", e => {
      fingers = e.touches.length;
      let nc = calculateCenter(e.touches);
      let dcx = nc[0] - center[0],
        dcy = nc[1] - center[1];

      if (fingers === 1) {
        this.addTranslate((-2 * dcx) / this.width, (2 * dcy) / this.height);
        center = nc;
      } else if (fingers === 2 && distance > 1) {
        let d = calculateDistance(e.touches);

        let tx = memory.translate[0],
          ty = memory.translate[1];
        let f = d / distance;
        let sx = memory.scale[0] / f,
          sy = memory.scale[1] / f;

        let rx = tx * f + (2 / this.width) * (center[0] * f - nc[0]),
          ry = ty * f - (2 / this.height) * (center[1] * f - nc[1]);

        this.scale = [sx, sy];
        this.translate = [rx, ry];
        this.invalidated = true;
      }
      e.preventDefault();
    });
    this.canvas.addEventListener("touchend", initializeTouch);
  }

  calculateDimensions() {
    let ow = this.width;
    let oh = this.height;
    let w = (this.width = this.canvas.clientWidth);
    let h = (this.height = this.canvas.clientHeight);
    if (w !== ow || h !== oh) {
      this.canvas.width = w;
      this.canvas.height = h;
      if (ow && oh) {
        this.scale[0] *= w / ow;
        this.scale[1] *= h / oh;
      } else {
        this.scale[0] *= Math.max(1, w / h);
        this.scale[1] *= Math.max(1, h / w);
      }
      this.invalidated = true;
    }
  }

  multiplyScale(s) {
    this.scale[0] *= s;
    this.scale[1] *= s;
    this.translate[0] /= s;
    this.translate[1] /= s;
    this.invalidated = true;
  }

  setScale(sx, sy) {
    this.scale[0] = sx;
    this.scale[1] = sy;
    this.invalidated = true;
  }

  setTranslate(x, y) {
    this.translate[0] = x;
    this.translate[1] = y;
    this.invalidated = true;
  }

  addTranslate(x, y) {
    this.translate[0] += x;
    this.translate[1] += y;
    this.invalidated = true;
  }

  offsetToCanvas(x, y) {
    return [
      ((x / this.width - 0.5) * 2 + this.translate[0]) * this.scale[0],
      ((0.5 - y / this.height) * 2 + this.translate[1]) * this.scale[1]
    ];
  }

  tick() {
    if (!this.disabled) window.requestAnimationFrame(() => this.tick());
    let now = +new Date() / 1000;
    let diff = now - this.time;
    if (this.time !== 0) this.fps.add(diff);
    this.time = now;

    this.calculateDimensions();
    if (this.invalidated && this.program) {
      this.invalidated = false;
      let gl = this.gl;

      if (this.props.onBeforeTick) {
        this.props.onBeforeTick(this, now, diff);
      }

      gl.useProgram(this.program);
      gl.viewport(0, 0, this.width, this.height); // gl.clear(gl.COLOR_BUFFER_BIT);

      gl.uniform2f(
        this.locations.translate,
        this.translate[0],
        this.translate[1]
      );
      gl.uniform2f(this.locations.scale, this.scale[0], this.scale[1]);

      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      gl.flush();
    }
  }

  createFragmentShader(code, libs) {
    let script = `
			precision highp float;
			precision highp int;

			varying vec2 v;
			uniform vec2 scale;
			uniform vec2 translate;
			`;
    if (libs)
      libs.forEach(lib => {
        if (packages[lib]) script += packages[lib];
      });
    script += code;

    let gl = this.gl;
    let shader = gl.createShader(gl.FRAGMENT_SHADER);
    gl.shaderSource(shader, script);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error("Error in shader: ", gl.getShaderInfoLog(shader));
      console.log(script);
      return false;
    }
    return shader;
  }

  calculateSize() {
    this.width = this.canvas.clientWidth;
    this.height = this.canvas.clientHeight;
  }

  invalidate() {
    this.invalidated = true;
  }

  render() {
    this.invalidated = true;
    return (
      <div
        style={{ width: "100%", height: "100%" }}
        ref={v => {
          if (v) v.appendChild(this.canvas);
        }}
      />
    );
  }
}
